import { useQuery } from '@tanstack/vue-query'
import { type Ref, computed } from 'vue'

import { useUrl } from '@/composables/url-helpers'
import { useI18nAddon } from '@/i18n/i18n'
import { usePersisterFn } from '@/query/persister-plugin'
import { Persisters } from '@/query/shared'
import { type CategoryType, RedirectMechanism, RedirectReason } from '@/types'
import { useAPI } from '@/utils/requests/api-plugin'

type UseCategorySeoInfoParams = {
    categoryName: Ref<string>
    pageType: Ref<CategoryType>
}

export const useCategorySeoInfo = ({ categoryName, pageType }: UseCategorySeoInfoParams) => {
    const { getCategoryRoute } = useUrl()
    const { currentLocale } = useI18nAddon()
    const { getGamesCategoriesSeoById } = useAPI()

    const queryKey = computed(() => ['category-seo-info', categoryName.value, pageType.value, currentLocale.value])
    const persister = usePersisterFn(Persisters.MediumCardinality)

    const { data, suspense, isError } = useQuery({
        queryKey,
        queryFn: async () => {
            const result = await getGamesCategoriesSeoById(categoryName.value, currentLocale.value, pageType.value)

            if (result.status === 'error') {
                throw new Error(result.message)
            }

            if (result.status === 'redirect') {
                return {
                    categoryRedirect: result.redirect,
                }
            }

            if (result.status !== 'ok') {
                return { fetchStatus: result.httpStatus }
            }

            if (result.data.categoryType !== pageType.value) {
                return {
                    categoryRedirect: {
                        redirectTarget: {
                            type: 'category',
                            cat_type: result.data.categoryType,
                            cat_hru: categoryName.value,
                            reason: RedirectReason.CATEGORY_TYPE_MISMATCH,
                            mechanism: RedirectMechanism.FRONTEND_REDIRECT,
                        },
                        status: 301,
                    } as const,
                }
            }

            const getSubCategories = (type: 'pathCategories' | 'subCategories') =>
                result.data[type]?.map((subCategory) => ({
                    ...subCategory,
                    href: getCategoryRoute({ hru: subCategory.hru, type: subCategory.categoryType }),
                })) || []

            return {
                categoryInfo: {
                    ...result.data,
                    pathCategories: getSubCategories('pathCategories'),
                    subCategories: getSubCategories('subCategories'),
                },
            }
        },
        persister,
    })

    return {
        categoryInfo: computed(() => data.value?.categoryInfo),
        categoryRedirect: computed(() => data.value?.categoryRedirect),
        fetchStatus: computed(() => data.value?.fetchStatus),
        hasError: isError,
        suspense,
    }
}
